/**
 * Per-event timeline hook. Self-managed state — NOT wired into the global
 * WeddingContext (see the rationale in useSeating.js).
 *
 * Usage: `const timeline = useTimeline(weddingId, eventId);`
 */
import { useCallback, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useTimeline(weddingId, eventId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!weddingId || !eventId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/timeline/${weddingId}/${eventId}`, { withCredentials: true });
      setItems(data);
    } catch (err) {
      console.error('Error loading timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [weddingId, eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  const createItem = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/timeline`, payload, { withCredentials: true });
    setItems((prev) => [...prev, data].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
    return data;
  }, []);

  const updateItem = useCallback(async (itemId, payload) => {
    const { data } = await axios.put(`${API}/timeline/${itemId}`, payload, { withCredentials: true });
    setItems((prev) => prev.map((i) => (i.id === itemId ? data : i)));
    return data;
  }, []);

  const deleteItem = useCallback(async (itemId) => {
    await axios.delete(`${API}/timeline/${itemId}`, { withCredentials: true });
    setItems((prev) => prev.filter((i) => i.id !== itemId));
  }, []);

  const reorder = useCallback(async (orderedIds) => {
    await axios.put(`${API}/timeline/reorder/${eventId}`, { item_ids: orderedIds }, { withCredentials: true });
    await refresh();
  }, [eventId, refresh]);

  return { items, loading, refresh, createItem, updateItem, deleteItem, reorder };
}
